import React, { useRef } from 'react';
import DataLoader from '../../../../shared/DataLoader';
import ErrorsWrapper from '../../../../shared/uibuilder/form/input/layout/ErrorsWrapper';
import styles from '../TransactionParties.module.scss';
import RelativeRow, { Cell, EmptyCell } from '../../../../shared/web/uitheme/form/RelativeRow';
import useTransactionPartiesField from '../transactionPartiesField';
import { CheckboxInput, useFormContext } from 'ui-builder';
import { PlanTask, ProjectRole, useProjectRolesService } from 'nekst-api';
import { BriefcaseCheckbox } from 'features/nekst-widgets';

function useInviteRecipientsField() {
  const formContext = useFormContext<PlanTask>();

  const isRoleAssigned = (id: number) => {
    const currentValue = formContext.data?.inviteRecipients || [];
    return !!currentValue.find((item) => item.projectRoleId === id);
  };

  const changeValue = (id: number) => {
    let newValue = formContext.data?.inviteRecipients || [];
    if (!isRoleAssigned(id)) {
      newValue.push({
        projectRoleId: id,
        _projectRole: {
          id,
          name: '',
        }
      });
    } else {
      newValue = newValue.filter((item) => item.projectRoleId !== id);
    }

    formContext.onChangeCallback!({
      inviteRecipients: newValue
    });
  };

  return {
    isRoleAssigned,
    changeValue
  };
}

function EventInviteRecipientsInputWidget(props: { projectRoles: ProjectRole[], label?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  const transactionPartiesField = useTransactionPartiesField(props.projectRoles);
  const inviteRecipientsField = useInviteRecipientsField();

  return (
    <ErrorsWrapper name="inviteRecipients">
      <ErrorsWrapper name="transactionParties">
        <div className={styles.block} ref={ref}>
          <div className={styles.title}>
            {props.label}
          </div>
          <div className={styles.table}>
            <RelativeRow
              weights={[6, 3, 3]}
              rowClassName={styles.tableHead}
              cellClassName={styles.cell}
            >
              <Cell>
                Assigned To:
              </Cell>
              <EmptyCell />
              <Cell centered>
                Receive Invite:
              </Cell>
            </RelativeRow>
            <div className={styles.tableBody}>
              {props.projectRoles.map(
                (projectRole) => {
                  return (
                    <RelativeRow
                      key={`projectRole-${projectRole.id}`}
                      weights={[6, 3, 3]}
                      rowClassName={styles.row}
                      cellClassName={styles.cell}
                    >
                      {projectRole.isAssignable ? (
                        <BriefcaseCheckbox
                          source={`assigned.${projectRole.id}`}
                          label={projectRole.name}
                          value={transactionPartiesField.isRoleAssigned(projectRole.id)}
                          onChangeCallback={() => {
                            transactionPartiesField.changeValue(projectRole.id);
                          }}
                        />
                      ) : (<Cell className={styles.noAssignment}>{projectRole.name}</Cell>)}
                      <EmptyCell />
                      <Cell centered className={styles.checkboxCell}>
                        <CheckboxInput
                          source={`receiveInvite.${projectRole.id}`}
                          label=""
                          value={inviteRecipientsField.isRoleAssigned(projectRole.id)}
                          onChangeCallback={() => {
                            inviteRecipientsField.changeValue(projectRole.id);
                          }}
                        />
                      </Cell>
                    </RelativeRow>
                  );
                }
              )}
            </div>
          </div>
        </div>
      </ErrorsWrapper>
    </ErrorsWrapper>
  );
}

export default function EventInviteRecipientsInput(props: { planId: number }) {
  const projectRolesService = useProjectRolesService();

  return (
    <DataLoader getDataFunc={projectRolesService.getAllTeamRoles}>
      {(projectRoles) => {
        return (<EventInviteRecipientsInputWidget projectRoles={projectRoles} label="Invite Recipients" />);
      }}
    </DataLoader>
  );

}
